import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, CheckCircle2, RefreshCw, Play } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Operation {
  id: string;
  status: string;
  generated_count: number;
  total_target: number;
  started_at: string;
  error_message: string | null;
}

export const OperationRecovery: React.FC = () => {
  const { toast } = useToast();
  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadOperations();
  }, []);

  const loadOperations = async () => {
    try {
      const { data, error } = await supabase
        .from('question_generation_operations')
        .select('*')
        .in('status', ['failed', 'stopped'])
        .order('started_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setOperations(data || []);
    } catch (error: any) {
      console.error('Error loading operations:', error);
    }
  };

  const resumeOperation = async (operationId: string) => {
    setLoading(true);
    try {
      // Mark as stopped to allow auto-manager to resume
      const { error: updateError } = await supabase
        .from('question_generation_operations')
        .update({ status: 'stopped', error_message: null })
        .eq('id', operationId);

      if (updateError) throw updateError;

      toast({
        title: 'Operation Resumed',
        description: 'The auto-manager will continue this operation shortly',
      });

      loadOperations();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const cleanupDuplicateRoleTypes = async () => {
    setLoading(true);
    try {
      // Fix role_type inconsistencies (e.g., "fullstack" vs "fullstack-developer")
      const roleMapping: Record<string, string> = {
        'fullstack': 'fullstack-developer',
        'frontend': 'frontend-developer',
        'backend': 'backend-developer',
        'devops': 'devops-engineer',
        'qa': 'qa-engineer',
        'security': 'security-engineer',
        'data': 'data-engineer'
      };

      let totalUpdated = 0;

      for (const [oldRole, newRole] of Object.entries(roleMapping)) {
        const { data, error } = await supabase
          .from('interview_questions')
          .update({ role_type: newRole })
          .eq('role_type', oldRole)
          .select('id');

        if (error) throw error;
        totalUpdated += data?.length || 0;
      }

      toast({
        title: 'Role Types Normalized',
        description: `Updated ${totalUpdated} questions to use consistent role types`,
      });

      loadOperations();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Operation Recovery & Cleanup
        </CardTitle>
        <CardDescription>
          Resume failed operations and clean up data inconsistencies
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Quick Actions */}
        <div className="flex gap-4">
          <Button onClick={loadOperations} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button onClick={cleanupDuplicateRoleTypes} disabled={loading} variant="secondary">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Normalize Role Types
          </Button>
        </div>

        {/* Failed Operations List */}
        <div className="space-y-3">
          <h3 className="font-semibold">Failed/Stopped Operations</h3>
          
          {operations.length === 0 ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                No failed or stopped operations found
              </AlertDescription>
            </Alert>
          ) : (
            operations.map((op) => (
              <Card key={op.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={op.status === 'failed' ? 'destructive' : 'secondary'}>
                          {op.status}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {new Date(op.started_at).toLocaleString()}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          Progress: {op.generated_count.toLocaleString()} / {op.total_target.toLocaleString()}
                          <span className="text-muted-foreground ml-2">
                            ({((op.generated_count / op.total_target) * 100).toFixed(1)}%)
                          </span>
                        </div>
                        
                        {op.error_message && (
                          <Alert variant="destructive" className="mt-2">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription className="text-xs">
                              {op.error_message}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => resumeOperation(op.id)}
                      disabled={loading}
                      size="sm"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Resume
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Info */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>How Recovery Works</AlertTitle>
          <AlertDescription>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Clicking "Resume" marks the operation as "stopped"</li>
              <li>The auto-manager detects stopped operations and resumes them automatically</li>
              <li>Generation continues from the last saved progress</li>
              <li>No questions are lost or duplicated</li>
            </ul>
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};
